import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type CampaignTypes,
  useCampaignActions,
  useCampaignDataAttributes,
  useSelectedCampaignId,
} from 'features/campaigns';
import { campaignMapper } from 'features/campaigns/services/campaignCardService';
import { campaignTabsMapper } from 'features/campaigns/services/campaignTabsService';
import CampaignCard from '../defaults/CampaignCard/CampaignCard';
import CampaignTabs from '../defaults/CampaignTabs/CampaignTabs';
import CampaignsCarousel from '../defaults/CampaignsCarousel/CampaignsCarousel';
import { getSectionColors } from '../../utils/campaignHelpers';
import { useItemsByCampaignType } from '../../hooks/data/useItemsByCampaignType';
import { useAppStore } from '../../../../store/app/appStore';
import '../../styles/campaign-variables.module.css';
import singleViewCampaignStyles from './css/SingleViewCampaign.module.css';

interface ICampaignWrapperProps {
  campaignType: CampaignTypes;
}

const SingleViewCampaignWrapper: React.FC<ICampaignWrapperProps> = ({ campaignType }) => {
  const dataWrapper = useItemsByCampaignType(campaignType);
  const { setCampaignType } = useCampaignActions();
  const dataAttributes = useCampaignDataAttributes({ campaignType });
  const { t } = useTranslation();
  const selectedCampaignId = useSelectedCampaignId(campaignType);
  const campaignOnlyType = useAppStore((state) => state.settings.campaignOnly?.type);

  useEffect(() => {
    // In ingame campaign-only mode only the opened type owns the global type.
    if (!campaignOnlyType || campaignOnlyType === campaignType) {
      setCampaignType(campaignType);
    }
  }, [campaignType, setCampaignType, campaignOnlyType]);

  const mappedCampaignData = !dataWrapper.hasMany ? campaignMapper(campaignType, dataWrapper.items) : null;
  const mappedTabsCampaignData = useMemo(() => campaignTabsMapper(campaignType, t), [campaignType, t]);

  const sectionColors = useMemo(
    () => getSectionColors(dataWrapper.items, selectedCampaignId, campaignType),
    [dataWrapper.items, selectedCampaignId, campaignType],
  );

  return (
      <div className={singleViewCampaignStyles.singleViewCampaignContainer} {...dataAttributes} style={sectionColors}>
        {/* CampaignCard can be split for single campaign to show one card or grid of campaigns cards*/}
        {mappedCampaignData === null ? (
          <CampaignsCarousel campaigns={dataWrapper.items} />
        ) : (
          <CampaignCard campaignInfo={mappedCampaignData.campaignInfo} />
        )}
        {/* {dataWrapper.hasMany && <div className={singleViewCampaignStyles.glowBox}></div>} */}
        <CampaignTabs tabsData={mappedTabsCampaignData.tabs} selectedCampaignId={selectedCampaignId} />
      </div>
  );
};

export default SingleViewCampaignWrapper;
